import React, { useEffect, useState, useRef } from 'react';
import api from '../api';
import {
  RefreshCw, Activity, Users, Globe2, BarChart3, Cpu, Radio,
} from 'lucide-react';

const RANGES = [
  { id: '5m',  label: '5 min' },
  { id: '1h',  label: '1 h'   },
  { id: '6h',  label: '6 h'   },
  { id: '24h', label: '24 h'  },
];

const TABS = [
  { id: 'overview', label: 'Visão Geral', icon: Activity },
  { id: 'clients',  label: 'Top Clientes', icon: Users },
  { id: 'domains',  label: 'Sites Acessados', icon: Globe2 },
  { id: 'live',     label: 'Logs ao Vivo', icon: Radio },
];

export default function Monitoring() {
  const [tab, setTab]     = useState('overview');
  const [range, setRange] = useState('1h');

  return (
    <div className="stack-lg">
      <header className="row-between">
        <div>
          <h1 className="h1">Monitor DNS</h1>
          <p className="dim" style={{ fontSize: 14, marginTop: 4 }}>
            Análise em tempo real das queries recebidas pelos servidores autoritativos.
          </p>
        </div>
        <div className="hrow" style={{ gap: 4, background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 2 }}>
          {RANGES.map(r => (
            <button key={r.id}
              onClick={() => setRange(r.id)}
              className={'btn btn-sm ' + (range === r.id ? 'btn-primary' : 'btn-ghost')}
              style={{ fontSize: 12 }}>
              {r.label}
            </button>
          ))}
        </div>
      </header>

      <div className="tab-bar">
        {TABS.map(t => {
          const Ic = t.icon;
          return (
            <div key={t.id} className={'tab ' + (tab === t.id ? 'active' : '')} onClick={() => setTab(t.id)}>
              <Ic size={14} style={{ marginRight: 6 }}/> {t.label}
            </div>
          );
        })}
      </div>

      {tab === 'overview' && <Overview range={range}/>}
      {tab === 'clients'  && <TopClients range={range}/>}
      {tab === 'domains'  && <TopDomains range={range}/>}
      {tab === 'live'     && <LiveLog/>}
    </div>
  );
}

// ─── Overview ───────────────────────────────────────────────────────
function Overview({ range }) {
  const [data, setData] = useState({ summary: null, timeseries: [], types: [], resolvers: [] });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, ts, qt, rv] = await Promise.all([
        api.get(`/dns/monitor/summary?range=${range}`),
        api.get(`/dns/monitor/timeseries?range=${range}&buckets=48`),
        api.get(`/dns/monitor/query-types?range=${range}`),
        api.get('/dns/monitor/resolvers'),
      ]);
      setData({
        summary: s.data,
        timeseries: ts.data.buckets || [],
        types: qt.data.types || [],
        resolvers: rv.data.resolvers || [],
      });
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [range]);

  const s = data.summary;

  return (
    <div className="stack">
      <div className="grid-4">
        <Kpi label="QPS (média)"      value={s?.qps_current ?? '–'}          spark={s?.qps_sparkline}/>
        <Kpi label="Total de queries" value={s ? s.total_queries.toLocaleString('pt-BR') : '–'}/>
        <Kpi label="Clientes únicos"  value={s?.clients_unique ?? '–'}/>
        <Kpi label="Janela analisada" value={s ? formatSpan(s.span_seconds) : '–'}/>
      </div>

      <div className="card">
        <div className="card-header">
          <BarChart3 size={15} color="var(--accent)"/>
          <h3 className="h3">Volume de queries · {range}</h3>
          <div className="card-actions">
            <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}><RefreshCw size={12}/> Atualizar</button>
          </div>
        </div>
        <div className="card-body">
          {data.timeseries.length > 0
            ? <AreaChart buckets={data.timeseries}/>
            : <div className="empty-state" style={{padding:32}}><div className="empty-desc">Sem dados no período</div></div>}
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-header">
            <Activity size={15} color="var(--accent)"/>
            <h3 className="h3">Tipos de query</h3>
            <span className="badge muted" style={{marginLeft:'auto'}}>{data.types.length} tipos</span>
          </div>
          <div className="card-body">
            {data.types.length > 0 ? <TypesList types={data.types}/> : <div className="dim">Sem dados</div>}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <Cpu size={15} color="var(--accent)"/>
            <h3 className="h3">Resolvers</h3>
          </div>
          <div className="card-body stack-sm">
            {data.resolvers.map(r => (
              <div key={r.name} style={{padding:'10px 12px', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:8}}>
                <div className="row-between">
                  <div>
                    <div style={{fontSize:13, fontWeight:600}}>{r.name}</div>
                    <div className="mono dim" style={{fontSize:11, marginTop:2}}>{r.ip} · {r.type}</div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div className="mono" style={{fontSize:18, fontWeight:700}}>{r.qps}</div>
                    <div className="dim" style={{fontSize:10.5}}>qps últimos 60s</div>
                  </div>
                </div>
                <div className="hrow" style={{gap:10, marginTop:8, fontSize:11}}>
                  <span className={'badge ' + (r.status === 'ok' ? 'ok' : 'err')}><span className={'dot ' + r.status}/> {r.status === 'ok' ? 'Online' : 'Falha'}</span>
                  <span className="dim">total: <span className="mono">{r.total_queries.toLocaleString('pt-BR')}</span></span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, spark }) {
  return (
    <div className="stat-card">
      <span className="stat-label">{label}</span>
      <div className="stat-value">{value}</div>
      {spark && spark.length > 0 && <Sparkline data={spark} height={28} color="var(--accent)"/>}
    </div>
  );
}

function Sparkline({ data, height = 32, color = 'var(--accent)' }) {
  if (!data || data.length < 2) return <div style={{height}}/>;
  const max = Math.max(...data, 1);
  const w = 100, h = height;
  const pts = data.map((v, i) => `${(i / (data.length - 1) * w).toFixed(2)},${(h - (v / max) * h).toFixed(2)}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" width="100%" height={h} style={{marginTop:4}}>
      <polyline fill={color} fillOpacity=".15" stroke="none" points={`0,${h} ${pts} ${w},${h}`}/>
      <polyline fill="none" stroke={color} strokeWidth="1.5" points={pts}/>
    </svg>
  );
}

function AreaChart({ buckets }) {
  if (!buckets || buckets.length < 2) return null;
  const w = 800, h = 200, pad = 28;
  const vals = buckets.map(b => b.total);
  const max = Math.max(...vals, 1);
  const xStep = (w - pad*2) / (buckets.length - 1);
  const pts = buckets.map((b, i) => `${pad + i*xStep},${h - pad - (b.total/max)*(h - pad*2)}`).join(' ');
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => ({
    y: h - pad - f*(h-pad*2),
    v: Math.round(max * f),
  }));
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" preserveAspectRatio="none" style={{height:200}}>
      {yTicks.map((t, i) => (
        <g key={i}>
          <line x1={pad} x2={w-pad} y1={t.y} y2={t.y} stroke="var(--border)" strokeWidth=".5"/>
          <text x={pad-4} y={t.y+3} textAnchor="end" fontSize="9" fill="var(--text-mute)" fontFamily="JetBrains Mono">{t.v}</text>
        </g>
      ))}
      <polyline fill="var(--accent)" fillOpacity=".15" stroke="none"
        points={`${pad},${h-pad} ${pts} ${pad + (buckets.length-1)*xStep},${h-pad}`}/>
      <polyline fill="none" stroke="var(--accent)" strokeWidth="1.5" points={pts}/>
    </svg>
  );
}

function TypesList({ types }) {
  const colors = ['var(--accent)', 'var(--accent-2)', 'var(--ok)', 'var(--warn)', 'var(--info)', '#22d3ee', '#a855f7', '#f97316'];
  return (
    <div className="stack-sm">
      {types.slice(0, 8).map((t, i) => (
        <div key={t.type}>
          <div className="row-between" style={{fontSize:12.5}}>
            <span className="mono" style={{fontWeight:600}}>{t.type}</span>
            <span className="dim">{t.count.toLocaleString('pt-BR')} · <span style={{color:'var(--text)'}}>{t.pct}%</span></span>
          </div>
          <div style={{height:6, background:'var(--bg)', borderRadius:3, overflow:'hidden', marginTop:4}}>
            <div style={{height:'100%', width: `${t.pct}%`, background: colors[i % colors.length], borderRadius:3, transition:'width .3s'}}/>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Top Clientes ───────────────────────────────────────────────────
function TopClients({ range }) {
  const [data, setData] = useState({ clients: [], total: 0 });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/dns/monitor/top-clients?range=${range}&limit=50`);
      setData(r.data);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [range]);

  return (
    <div className="card">
      <div className="card-header">
        <Users size={15} color="var(--accent)"/>
        <h3 className="h3">Top 50 clientes em {range}</h3>
        <span className="badge muted">{data.total.toLocaleString('pt-BR')} queries totais</span>
        <div className="card-actions">
          <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}><RefreshCw size={12}/></button>
        </div>
      </div>
      {data.clients.length === 0 ? (
        <div className="empty-state"><div className="empty-desc">Sem queries no período</div></div>
      ) : (
        <table className="table">
          <thead><tr>
            <th style={{width:40}}>#</th>
            <th>IP do cliente</th>
            <th>Queries</th>
            <th>Tipo principal</th>
            <th style={{width:200}}>Participação</th>
          </tr></thead>
          <tbody>
            {data.clients.map((c, i) => (
              <tr key={c.ip}>
                <td className="mono dim">{i+1}</td>
                <td className="mono" style={{fontWeight:600}}>{c.ip}</td>
                <td className="mono">{c.queries_total.toLocaleString('pt-BR')}</td>
                <td><span className="badge accent mono">{c.top_type}</span></td>
                <td>
                  <div className="hrow" style={{gap:8}}>
                    <div style={{flex:1, height:6, background:'var(--bg)', borderRadius:3, overflow:'hidden'}}>
                      <div style={{height:'100%', width:`${Math.min(100, c.share_pct*4)}%`, background:'var(--accent)', borderRadius:3}}/>
                    </div>
                    <span className="mono dim" style={{fontSize:11.5, minWidth:48, textAlign:'right'}}>{c.share_pct}%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Top Domínios ──────────────────────────────────────────────────
function TopDomains({ range }) {
  const [data, setData] = useState({ domains: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/dns/monitor/top-domains?range=${range}&limit=100`);
      setData(r.data);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [range]);

  const filtered = q ? data.domains.filter(d => d.domain.toLowerCase().includes(q.toLowerCase())) : data.domains;

  return (
    <div className="card">
      <div className="card-header">
        <Globe2 size={15} color="var(--accent)"/>
        <h3 className="h3">Top 100 domínios em {range}</h3>
        <span className="badge muted">{data.total.toLocaleString('pt-BR')} queries</span>
        <div className="card-actions">
          <input className="input" value={q} onChange={e=>setQ(e.target.value)} placeholder="Filtrar…"
            style={{width:200, padding:'5px 10px', fontSize:12}}/>
          <button className="btn btn-ghost btn-sm" onClick={load} disabled={loading}><RefreshCw size={12}/></button>
        </div>
      </div>
      {filtered.length === 0 ? (
        <div className="empty-state"><div className="empty-desc">Sem dados</div></div>
      ) : (
        <table className="table">
          <thead><tr>
            <th style={{width:40}}>#</th>
            <th>Domínio</th>
            <th>Queries</th>
            <th>Tipo</th>
            <th style={{width:140}}>Share</th>
          </tr></thead>
          <tbody>
            {filtered.slice(0, 100).map((d, i) => (
              <tr key={d.domain}>
                <td className="mono dim">{i+1}</td>
                <td className="mono" style={{fontSize:12.5}}>{d.domain}</td>
                <td className="mono">{d.count.toLocaleString('pt-BR')}</td>
                <td><span className="badge muted mono">{d.top_type}</span></td>
                <td className="mono dim">{d.share_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─── Live Log ──────────────────────────────────────────────────────
function LiveLog() {
  const [queries, setQueries] = useState([]);
  const [paused, setPaused]   = useState(false);
  const seenRef = useRef(new Set());

  const tick = async () => {
    if (paused) return;
    try {
      const r = await api.get('/dns/monitor/recent?limit=80');
      const newOnes = (r.data.queries || []).filter(q => {
        const key = `${q.timestamp}|${q.client_ip}|${q.query_name}|${q.query_type}`;
        if (seenRef.current.has(key)) return false;
        seenRef.current.add(key);
        return true;
      });
      if (newOnes.length > 0) {
        setQueries(prev => [...newOnes, ...prev].slice(0, 200));
        if (seenRef.current.size > 500) {
          seenRef.current = new Set(Array.from(seenRef.current).slice(-300));
        }
      }
    } catch {}
  };
  useEffect(() => { tick(); const i = setInterval(tick, 3000); return () => clearInterval(i); }, [paused]);

  const typeColors = { A:'accent', AAAA:'accent', PTR:'violet', MX:'ok', TXT:'muted', NS:'info', CNAME:'warn', SRV:'info', CAA:'warn' };

  return (
    <div className="card">
      <div className="card-header">
        <Radio size={15} color={paused ? 'var(--text-mute)' : 'var(--ok)'}/>
        <h3 className="h3">Streaming · queries em tempo real</h3>
        {!paused && <span className="badge ok"><span className="dot ok pulse"/>LIVE</span>}
        <span className="badge muted">{queries.length} na tela</span>
        <div className="card-actions">
          <button className="btn btn-secondary btn-sm" onClick={()=>setPaused(p=>!p)}>
            {paused ? 'Retomar' : 'Pausar'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={()=>{setQueries([]); seenRef.current.clear();}}>Limpar</button>
        </div>
      </div>
      <div style={{maxHeight:520, overflow:'auto', padding:'4px 0'}}>
        {queries.length === 0 ? (
          <div className="empty-state" style={{padding:48}}>
            <div className="empty-desc">Aguardando queries…</div>
          </div>
        ) : queries.map((q, i) => (
          <div key={i} className="hrow" style={{
            padding:'7px 18px', borderBottom:'1px solid var(--border)',
            fontFamily:'var(--font-mono)', fontSize:11.5, gap:14,
          }}>
            <span style={{color:'var(--text-mute)', minWidth:90}}>{q.timestamp.split(' ')[1]?.slice(0,12) || q.timestamp}</span>
            <span className={`badge ${typeColors[q.query_type] || 'muted'} mono`} style={{fontSize:10, minWidth:40, justifyContent:'center'}}>{q.query_type}</span>
            <span style={{color:'var(--accent)', minWidth:130}}>{q.client_ip}</span>
            <span style={{flex:1, color:'var(--text)', wordBreak:'break-all'}}>{q.query_name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatSpan(seconds) {
  if (!seconds) return '–';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds/60)} min`;
  if (seconds < 86400) return `${(seconds/3600).toFixed(1)} h`;
  return `${(seconds/86400).toFixed(1)} d`;
}
